import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Plus, Upload, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { TemplateDrivenMaterialInfoSection } from '@/components/materials/TemplateDrivenMaterialInfoSection'
import { PurchaseSection } from '@/components/materials/PurchaseSection'
import { useMaterialForm } from '@/components/materials/useMaterialForm'
import { apiClient } from '@/services/api'

export const AddMaterialPage: React.FC = () => {
  const navigate = useNavigate()
  const {
    formData,
    errors,
    conversionLoading,
    handleInputChange,
    validateForm,
    getUnitCategory,
    getRecommendedUnits
  } = useMaterialForm()

  const [materialImages, setMaterialImages] = useState<File[]>([])
  const [receiptImages, setReceiptImages] = useState<File[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const handleImageUpload = (files: FileList | null, type: 'material' | 'receipt') => {
    if (!files) return
    
    const newFiles = Array.from(files).filter(file => {
      if (!file.type.startsWith('image/')) {
        alert('Please select only image files')
        return false
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB')
        return false
      }
      return true
    })

    if (type === 'material') {
      setMaterialImages(prev => [...prev, ...newFiles])
    } else {
      setReceiptImages(prev => [...prev, ...newFiles])
    }
  }

  const removeImage = (index: number, type: 'material' | 'receipt') => {
    if (type === 'material') {
      setMaterialImages(prev => prev.filter((_, i) => i !== index))
    } else {
      setReceiptImages(prev => prev.filter((_, i) => i !== index))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsLoading(true)
    try {
      // Prepare form data for API
      const materialData = {
        name: formData.name,
        categoryId: formData.category, // Backend expects 'categoryId'
        baseUnit: formData.baseUnit,
        sku: formData.sku,
        description: formData.description,
        specifications: formData.specifications,
        brand: formData.brand,
        reorderPoint: formData.reorderPoint,
        maxStock: formData.maxStock,
        // Purchase information if included
        ...(formData.includePurchase && {
          // Always send the package purchase flag
          isPackagePurchase: formData.isPackagePurchase,
          
          // Package purchase fields (for package/batch units)
          ...(formData.isPackagePurchase && {
            packageQuantity: formData.packageQuantity,
            itemsPerPackage: formData.itemsPerPackage,
            packageCost: formData.packageCost,
          }),
          // Regular purchase fields (for non-package units)
          ...(!formData.isPackagePurchase && {
            purchaseQuantity: formData.purchaseQuantity,
            purchaseUnit: formData.purchaseUnit,
            costPerUnit: formData.costPerUnit,
            totalCost: formData.totalCost,
            costInputType: formData.costInputType,
          }),
          // Common purchase fields
          currentStock: formData.convertedQuantity, // Stock in base unit
          supplier: formData.supplier,
          purchaseDate: formData.purchaseDate,
          invoiceNumber: formData.invoiceNumber,
          notes: formData.notes
        })
      }
      
      console.log('Sending material data:', materialData) // Debug log

      // Call the appropriate materials API endpoint
      const endpoint = formData.includePurchase ? '/materials/with-purchase' : '/materials'
      const result = await apiClient.post(endpoint, materialData)
      console.log('Material created:', result)
      
      // TODO: Handle image uploads if any images were selected
      if (materialImages.length > 0 || receiptImages.length > 0) {
        console.log('Image upload functionality to be implemented')
      }
      
      alert('Material created successfully!')
      navigate('/materials')
    } catch (error) {
      console.error('Error creating material:', error)
      alert('Error creating material. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/materials')}
          leftIcon={<ArrowLeft className="w-4 h-4" />}
        >
          Back to Materials
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Add Material
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Add a new material to your inventory with automatic unit conversion
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Template-Driven Material Information Section */}
        <TemplateDrivenMaterialInfoSection
          formData={formData}
          onInputChange={handleInputChange}
          errors={errors}
        />

        {/* Material Images */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Material Images (Optional)
          </h3>
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6">
            <div className="text-center">
              <Upload className="mx-auto h-12 w-12 text-gray-400" />
              <div className="mt-4">
                <label className="cursor-pointer">
                  <span className="mt-2 block text-sm font-medium text-gray-900 dark:text-white">
                    Upload material images
                  </span>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e.target.files, 'material')}
                  />
                </label>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                PNG, JPG, GIF up to 5MB each
              </p>
            </div>
          </div>
          
          {materialImages.length > 0 && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
              {materialImages.map((file, index) => (
                <div key={index} className="relative">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Material ${index + 1}`}
                    className="w-full h-24 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index, 'material')}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Purchase Information Section */}
        <PurchaseSection
          formData={formData}
          onInputChange={handleInputChange}
          errors={errors}
          conversionLoading={conversionLoading}
          getUnitCategory={getUnitCategory}
          getRecommendedUnits={getRecommendedUnits}
        />

        {/* Receipt Images (only show if purchase is included) */}
        {formData.includePurchase && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Receipt Images (Optional)
            </h3>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6">
              <div className="text-center">
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <div className="mt-4">
                  <label className="cursor-pointer">
                    <span className="mt-2 block text-sm font-medium text-gray-900 dark:text-white">
                      Upload receipt images
                    </span>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e.target.files, 'receipt')}
                    />
                  </label>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  PNG, JPG, GIF up to 5MB each
                </p>
              </div>
            </div>
            
            {receiptImages.length > 0 && (
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                {receiptImages.map((file, index) => (
                  <div key={index} className="relative">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`Receipt ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index, 'receipt')}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Submit Buttons */}
        <div className="flex gap-4 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/materials')}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isLoading}
            leftIcon={isLoading ? undefined : <Plus className="w-4 h-4" />}
          >
            {isLoading ? 'Creating Material...' : 'Create Material'}
          </Button>
        </div>
      </form>
    </div>
  )
}